/**
 * registry-delivery-choice.js — CUR-158. ONE source for how gifts ship:
 *   • the three options, in one wording (the delivery page, the progress
 *     banner, the guided summary and the publish flows all read OPTIONS);
 *   • whether the parent has actually chosen (hasChoice — the same rule as
 *     birch-registry app/lib/deliveryChoice.js, which gates publishing);
 *   • WHY a gift is held (heldReason / externalReason), from the data the
 *     registry API serves: purchase.holds[] {releasedAt, scheduledShipAt,
 *     parentChoice} and ExternalOrder {status, releasedAt};
 *   • the "When should gifts ship?" card (cardHtml / mountCard).
 *
 * No preselected option: a new registry starts as ship_on_date with no date,
 * which is exactly the state that used to strand gifts with no release
 * schedule, so that default reads as "not chosen" everywhere.
 *
 * Exposes window.BirchDeliveryChoice. Pure except mountCard (DOM + API).
 */
(function () {
  'use strict';

  var OPTIONS = [
    { value: 'ship_on_date', title: 'Arrive in a week you choose', body: 'We hold each gift and ship it so it arrives around that week.' },
    { value: 'manual_release', title: 'I’ll release them', body: 'Gifts wait with us until you tap Ship now on each one.' },
    { value: 'ship_as_gifted', title: 'Ship right away', body: 'Each gift ships as soon as someone buys it.' },
  ];
  var VALUES = OPTIONS.map(function (o) { return o.value; });
  var DAY_MS = 24 * 60 * 60 * 1000;
  // How many days before the delivery week the team buys a gift from another
  // store — birch-registry app/lib/externalOrder.ts TRIGGER_ADVANCE_DAYS
  // (retailer SLA 14 + buffer 1). Keep in step.
  var EXTERNAL_ADVANCE_DAYS = 15;

  function toDate(v) {
    if (!v) return null;
    var d = v instanceof Date ? v : new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }

  // Dates are stored as UTC midnights (deliveryDate) or UTC instants
  // (scheduledShipAt); formatting in UTC keeps "Nov 1" from reading as "Oct 31"
  // west of Greenwich.
  var DAY_FMT = (typeof Intl !== 'undefined' && Intl.DateTimeFormat)
    ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : null;
  function fmtDay(v) {
    var d = toDate(v);
    if (!d) return '';
    return DAY_FMT ? DAY_FMT.format(d) : d.toISOString().slice(0, 10);
  }

  function isoDay(d) { return d.toISOString().slice(0, 10); }
  function todayIso(now) { return isoDay(now || new Date()); }
  function addDaysIso(iso, n) { return isoDay(new Date(Date.parse(iso + 'T00:00:00Z') + n * DAY_MS)); }

  /** The parent has chosen how gifts ship. The untouched default — a delivery
   *  week with no week picked — is not a choice. */
  function hasChoice(reg) {
    if (!reg) return false;
    var s = reg.deliverySetting;
    if (s === 'manual_release' || s === 'ship_as_gifted') return true;
    if (s === 'ship_on_date') return !!reg.deliveryDate;
    return false;
  }

  /** The banner above the registered gifts, or null when nothing is chosen
   *  yet (the card asks instead). */
  function modeCopy(reg) {
    if (!hasChoice(reg)) return null;
    if (reg.deliverySetting === 'ship_on_date') {
      return {
        title: 'Gifts arrive the week of ' + fmtDay(reg.deliveryDate),
        body: 'We hold each gift and ship it so it arrives around then. You can ship any gift sooner.',
      };
    }
    if (reg.deliverySetting === 'manual_release') {
      return { title: 'You release gifts yourself', body: 'Gifts wait with us until you tap Ship now on each one.' };
    }
    return { title: 'Gifts ship right away', body: 'Each gift ships as soon as someone buys it.' };
  }

  /** Why a Birch gift is held, in one sentence. */
  function heldReason(purchase, reg, now) {
    now = now || new Date();
    var open = ((purchase && purchase.holds) || []).filter(function (h) { return h && !h.releasedAt; });
    if (open.some(function (h) { return h.parentChoice === 'hold'; })) {
      return 'Kept with us until you tap Ship now, as you asked.';
    }
    var picked = open.filter(function (h) { return h.parentChoice === 'date' && toDate(h.scheduledShipAt); });
    if (picked.length) {
      var p = toDate(picked[0].scheduledShipAt);
      if (p <= now) return 'Ships within the next few hours.';
      return 'Ships ' + fmtDay(p) + ', the day you picked.';
    }
    var dated = open
      .map(function (h) { return toDate(h.scheduledShipAt); })
      .filter(Boolean)
      .sort(function (a, b) { return a - b; });
    if (dated.length) {
      if (dated[0] <= now) return 'Ships within the next few hours.';
      // A hold can carry a ship day while the registry has no delivery week.
      if (!toDate(reg && reg.deliveryDate)) return 'Ships around ' + fmtDay(dated[0]) + '.';
      return 'Ships around ' + fmtDay(dated[0]) + ' so it arrives the week of ' + fmtDay(reg && reg.deliveryDate) + '.';
    }
    var mode = reg && reg.deliverySetting;
    if (mode === 'manual_release') return 'Waiting for you. It ships when you tap Ship now.';
    if (mode === 'ship_on_date') {
      return reg.deliveryDate
        ? 'Waiting for your delivery week (' + fmtDay(reg.deliveryDate) + ').'
        : 'Waiting until you choose when gifts should ship.';
    }
    return 'Shipping soon.';
  }

  function externalBuyDate(reg) {
    var d = toDate(reg && reg.deliveryDate);
    return d ? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - EXTERNAL_ADVANCE_DAYS * DAY_MS) : null;
  }

  /** Is the team cleared to buy this gift from another store now? Mirrors the
   *  agent queue (birch-registry proxy.agent.external-orders._index.tsx). */
  function externalGoAhead(order, reg, now) {
    if (!order || order.status === 'flagged') return false;
    if (order.releasedAt) return true;
    var mode = reg && reg.deliverySetting;
    if (mode === 'ship_as_gifted') return true;
    if (mode === 'ship_on_date') {
      var buy = externalBuyDate(reg);
      if (!buy) return true; // no week: the queue buys it straight away
      var today = new Date(Date.parse(todayIso(now) + 'T00:00:00Z'));
      return today >= buy;
    }
    return false;
  }

  /** Why a gift from another store is waiting (or that it is being bought). */
  function externalReason(order, reg, now) {
    if (order && order.status === 'flagged') return 'Our team is checking this one before buying it.';
    if (externalGoAhead(order, reg, now)) return 'Our team is buying this now.';
    var mode = reg && reg.deliverySetting;
    if (mode === 'manual_release') return 'Waiting for you. It ships when you tap Ship now.';
    var buy = externalBuyDate(reg);
    if (buy) return 'We’ll buy this around ' + fmtDay(buy) + ' so it arrives the week of ' + fmtDay(reg.deliveryDate) + '.';
    return 'Waiting until you choose when gifts should ship.';
  }

  /** The body the card saves, or null when it is not savable yet. */
  function payloadFor(setting, dateIso) {
    if (VALUES.indexOf(setting) === -1) return null;
    if (setting === 'ship_on_date') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateIso || '')) return null;
      return { deliverySetting: setting, deliveryDate: dateIso };
    }
    return { deliverySetting: setting, deliveryDate: null };
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  var uid = 0;
  /** The "When should gifts ship?" card. Nothing is checked unless the
   *  registry already holds a real choice (then it is being edited). */
  function cardHtml(opts) {
    opts = opts || {};
    var reg = opts.registry || {};
    var today = opts.todayIso || todayIso();
    var tomorrow = addDaysIso(today, 1);
    var chosen = hasChoice(reg) ? reg.deliverySetting : null;
    var prefill = '';
    var regDay = toDate(reg.deliveryDate);
    var dueDay = toDate(reg.dueDate);
    if (chosen === 'ship_on_date' && regDay) prefill = isoDay(regDay);
    else if (dueDay && isoDay(dueDay) >= tomorrow) prefill = isoDay(dueDay);
    var name = 'bdc-choice-' + (++uid);
    var rows = OPTIONS.map(function (o) {
      return '<label class="bdc-option">' +
        '<input type="radio" name="' + name + '" value="' + o.value + '"' + (chosen === o.value ? ' checked' : '') + '>' +
        '<span class="bdc-option__text"><strong>' + esc(o.title) + '</strong><span>' + esc(o.body) + '</span></span>' +
        '</label>';
    }).join('');
    return '<div class="bdc" data-bdc>' +
      '<p class="bdc__title">' + esc(opts.title || 'When should gifts ship?') + '</p>' +
      (opts.intro ? '<p class="bdc__intro">' + esc(opts.intro) + '</p>' : '') +
      '<div class="bdc__options" role="radiogroup" aria-label="When should gifts ship?">' + rows + '</div>' +
      '<div class="bdc__week" data-bdc-week' + (chosen === 'ship_on_date' ? '' : ' hidden') + '>' +
        '<label class="bdc__week-label">Arriving from' +
          ' <input type="date" class="bdc__date" data-bdc-date min="' + tomorrow + '" value="' + esc(prefill) + '">' +
        '</label>' +
        '<span class="bdc__week-hint">We ship each gift ahead of time so it arrives in the 7 days from this date.</span>' +
      '</div>' +
      '<p class="bdc__error" data-bdc-error role="alert" hidden></p>' +
      '<button type="button" class="bdc__save" data-bdc-save disabled>' + esc(opts.saveLabel || 'Save') + '</button>' +
      '</div>';
  }

  var STYLE = '' +
    '.bdc{border:1px solid var(--reg-line,#E5DFD3);border-radius:14px;padding:18px;background:#fff;text-align:left}' +
    '.bdc__title{margin:0 0 4px;font-weight:600;font-size:1rem;color:var(--reg-ink,#1F2D26)}' +
    '.bdc__intro{margin:0 0 12px;font-size:.875rem;color:var(--reg-muted,#6B6558)}' +
    '.bdc__options{display:flex;flex-direction:column;gap:8px;margin:10px 0}' +
    '.bdc-option{display:flex;gap:10px;align-items:flex-start;padding:10px 12px;border:1px solid var(--reg-line,#E5DFD3);border-radius:10px;cursor:pointer}' +
    '.bdc-option:has(input:checked){border-color:var(--reg-ink,#1F2D26);box-shadow:0 0 0 1px var(--reg-ink,#1F2D26)}' +
    '.bdc-option input{margin-top:3px;accent-color:var(--reg-ink,#1F2D26)}' +
    '.bdc-option__text{display:flex;flex-direction:column;gap:2px;font-size:.875rem;color:var(--reg-muted,#6B6558)}' +
    '.bdc-option__text strong{color:var(--reg-ink,#1F2D26);font-weight:600}' +
    '.bdc__week{display:flex;flex-direction:column;gap:6px;margin:4px 0 10px}' +
    '.bdc__week[hidden]{display:none}' +
    '.bdc__week-label{font-size:.875rem;color:var(--reg-ink,#1F2D26);display:flex;gap:8px;align-items:center;flex-wrap:wrap}' +
    '.bdc__date{font:inherit;padding:8px 10px;border:1px solid var(--reg-line,#E5DFD3);border-radius:8px}' +
    '.bdc__week-hint{font-size:.8125rem;color:var(--reg-muted,#6B6558)}' +
    '.bdc__error{margin:0 0 8px;font-size:.8125rem;color:#9B2C2C}' +
    '.bdc__save{font:inherit;font-weight:600;padding:10px 20px;border-radius:999px;border:0;background:var(--reg-ink,#1F2D26);color:#fff;cursor:pointer}' +
    '.bdc__save[disabled]{opacity:.45;cursor:not-allowed}' +
    '.bdc__signin{font-size:.875rem;color:var(--reg-muted,#6B6558);margin:0}' +
    '.bdc__signin a{color:var(--reg-ink,#1F2D26);font-weight:600}';

  function ensureStyle() {
    if (typeof document === 'undefined' || document.getElementById('bdc-style')) return;
    var s = document.createElement('style');
    s.id = 'bdc-style';
    s.textContent = STYLE;
    document.head.appendChild(s);
  }

  /**
   * Render the card into `container` and save through the registry API.
   * opts: { registry, api, isGuest, signInUrl, title, intro, saveLabel, onSaved(registry),
   *         draft, onDraft(draft) }
   * `draft` / `onDraft` carry an unsaved pick ({ setting, date }) across a
   * re-mount: a host that repaints (the walkthrough summary does, when its
   * registry read lands) would otherwise wipe what the parent just ticked.
   * A guest cannot save a delivery choice (the API refuses), so a guest sees a
   * sign-in line instead of options it could not keep.
   */
  function mountCard(container, opts) {
    if (!container) return;
    ensureStyle();
    opts = opts || {};
    if (opts.isGuest) {
      container.innerHTML = '<div class="bdc" data-bdc>' +
        '<p class="bdc__title">' + esc(opts.title || 'When should gifts ship?') + '</p>' +
        '<p class="bdc__signin"><a href="' + esc(opts.signInUrl || '/account/login') + '">Sign in</a> to choose when gifts ship. It’s saved to your registry.</p>' +
        '</div>';
      return;
    }
    var reg = opts.registry || {};
    var saveLabel = opts.saveLabel || 'Save';
    container.innerHTML = cardHtml({ registry: reg, title: opts.title, intro: opts.intro, saveLabel: saveLabel });
    var root = container.querySelector('[data-bdc]');
    var week = root.querySelector('[data-bdc-week]');
    var dateInput = root.querySelector('[data-bdc-date]');
    var save = root.querySelector('[data-bdc-save]');
    var err = root.querySelector('[data-bdc-error]');
    var current = function () {
      var c = root.querySelector('input[type="radio"]:checked');
      return c ? c.value : null;
    };
    var sync = function () {
      var v = current();
      week.hidden = v !== 'ship_on_date';
      save.disabled = !payloadFor(v, dateInput.value);
      err.hidden = true;
    };
    var draft = opts.draft;
    if (draft && draft.setting) {
      var pick = root.querySelector('input[type="radio"][value="' + draft.setting + '"]');
      if (pick) pick.checked = true;
      if (draft.date) dateInput.value = draft.date;
    }
    var report = function () {
      sync();
      if (opts.onDraft) opts.onDraft({ setting: current(), date: dateInput.value });
    };
    root.addEventListener('change', report);
    dateInput.addEventListener('input', report);
    sync();
    save.addEventListener('click', function () {
      var body = payloadFor(current(), dateInput.value);
      if (!body) return;
      if (body.deliveryDate && body.deliveryDate <= todayIso()) {
        err.textContent = 'Pick a date after today.';
        err.hidden = false;
        return;
      }
      save.disabled = true;
      save.textContent = 'Saving…';
      Promise.resolve(opts.api.update(reg.id, body)).then(function (res) {
        var updated = (res && res.registry) || Object.assign({}, reg, body);
        if (opts.onSaved) opts.onSaved(updated);
      }).catch(function (e) {
        err.textContent = (e && e.message) || 'Couldn’t save that. Please try again.';
        err.hidden = false;
        save.disabled = false;
        save.textContent = saveLabel;
      });
    });
  }

  window.BirchDeliveryChoice = {
    OPTIONS: OPTIONS,
    hasChoice: hasChoice,
    modeCopy: modeCopy,
    heldReason: heldReason,
    externalGoAhead: externalGoAhead,
    externalReason: externalReason,
    payloadFor: payloadFor,
    cardHtml: cardHtml,
    mountCard: mountCard,
    fmtDay: fmtDay,
  };
})();
